import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import JuntoApiService, { Credit, ClassCategory } from '../services/api';
import EntityActionButtons from '../components/EntityActionButtons/EntityActionButtons';

const CreditsPage: React.FC = () => {
  const [credits, setCredits] = useState<Credit[]>([]);
  const [classCategories, setClassCategories] = useState<ClassCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [includeInactive, setIncludeInactive] = useState(false);
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCredit, setEditingCredit] = useState<Credit | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    cost: '',
    valid_for_days: '',
    valid_class_category_ids: [] as string[],
  });

  const loadCredits = useCallback(async () => {
    try {
      setLoading(true);
      const [creditsData, categoriesData] = await Promise.all([
        JuntoApiService.getCredits(includeInactive),
        JuntoApiService.getClassCategories(includeInactive)
      ]);
      setCredits(creditsData);
      setClassCategories(categoriesData);
      setError(null);
    } catch (err) {
      console.error('Failed to load credits:', err);
      setError('Failed to load credits');
    } finally {
      setLoading(false);
    }
  }, [includeInactive]);

  useEffect(() => {
    loadCredits();
  }, [loadCredits]);


  const getCategoryName = (categoryId: string): string => {
    const category = classCategories.find(cat => cat.id === categoryId);
    return category ? category.name : categoryId;
  };

  const openCreateDialog = () => {
    setEditingCredit(null);
    setFormData({
      name: '',
      description: '',
      cost: '',
      valid_for_days: '',
      valid_class_category_ids: [],
    });
    setDialogError(null);
    setDialogOpen(true);
  };

  const openEditDialog = (credit: Credit) => {
    setEditingCredit(credit);
    setFormData({
      name: credit.name,
      description: credit.description || '',
      cost: credit.cost ? credit.cost.toString() : '',
      valid_for_days: credit.valid_for_days?.toString() || '',
      valid_class_category_ids: credit.valid_class_category_ids,
    });
    setDialogError(null);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingCredit(null);
    setDialogError(null);
  };

  const handleSave = async () => {
    try {
      setDialogError(null);
      
      const cost = parseFloat(formData.cost);
      if (isNaN(cost) || cost < 0) {
        setDialogError('Cost must be a valid positive number');
        return;
      }

      if (editingCredit) {
        // Update existing credit
        const creditData = {
          ...formData,
          id: editingCredit.id,
          cost: cost,
          valid_for_days: formData.valid_for_days ? parseInt(formData.valid_for_days) : undefined,
          created_at: editingCredit.created_at
        };
        await JuntoApiService.updateCredit(creditData as Credit);
      } else {
        // Create new credit
        const creditData = {
          ...formData,
          cost: cost,
          valid_for_days: formData.valid_for_days ? parseInt(formData.valid_for_days) : undefined
        };
        await JuntoApiService.createCredit(creditData);
      }
      
      await loadCredits();
      closeDialog();
    } catch (err) {
      console.error('Failed to save credit:', err);
      setDialogError('Failed to save credit. Please check your input.');
    }
  };



  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading credits...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Credits</Typography>
        <Box display="flex" alignItems="center" gap={2}>
          <FormControlLabel
            control={
              <Switch
                checked={includeInactive}
                onChange={(e) => setIncludeInactive(e.target.checked)}
                icon={<VisibilityOffIcon />}
                checkedIcon={<VisibilityIcon />}
              />
            }
            label="Show Inactive"
          />
          <Button 
            variant="contained" 
            startIcon={<AddIcon />}
            onClick={openCreateDialog}
          >
            Create Credit
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
          <Button size="small" onClick={loadCredits} sx={{ ml: 2 }}>
            Retry
          </Button>
        </Alert>
      )}

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Cost</TableCell>
                <TableCell>Valid For</TableCell>
                <TableCell>Valid Categories</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {credits.map((credit) => (
                <TableRow 
                  key={credit.id} 
                  hover 
                  sx={{ 
                    opacity: credit.is_active ? 1 : 0.6,
                    backgroundColor: credit.is_active ? 'inherit' : 'action.hover'
                  }}
                >
                  <TableCell>
                    <Typography variant="body1" fontWeight="medium">
                      {credit.name}
                    </Typography>
                  </TableCell>
                  <TableCell>{credit.description}</TableCell>
                  <TableCell>${credit.cost ? credit.cost.toFixed(2) : 'N/A'}</TableCell>
                  <TableCell>
                    {credit.valid_for_days ? `${credit.valid_for_days} days` : 'No limit'}
                  </TableCell>
                  <TableCell>
                    {credit.valid_class_category_ids.map((catId) => (
                      <Chip
                        key={catId}
                        label={getCategoryName(catId)}
                        size="small"
                        variant="outlined"
                        sx={{ mr: 0.5 }}
                      />
                    ))}
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={credit.is_active ? "Active" : "Inactive"}
                      color={credit.is_active ? "success" : "default"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <EntityActionButtons
                      entity={credit}
                      entityType="credits"
                      entityDisplayName="Credit"
                      onEdit={openEditDialog}
                      onRefresh={loadCredits}
                      renderEntityInHistory={(entity) => (
                        <Box>
                          <Typography variant="body1">
                            <strong>Name:</strong> {entity.name}
                          </Typography>
                          {entity.description && (
                            <Typography variant="body1">
                              <strong>Description:</strong> {entity.description}
                            </Typography>
                          )}
                          <Typography variant="body1">
                            <strong>Cost:</strong> ${entity.cost}
                          </Typography>
                          <Typography variant="body1">
                            <strong>Valid For:</strong> {entity.valid_for_days ? `${entity.valid_for_days} days` : 'No limit'}
                          </Typography>
                          <Typography variant="body1">
                            <strong>Active:</strong> {entity.is_active ? 'Yes' : 'No'}
                          </Typography>
                        </Box>
                      )}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingCredit ? 'Edit Credit' : 'Create New Credit'}
        </DialogTitle>
        <DialogContent>
          {dialogError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {dialogError}
            </Alert>
          )}
          
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Cost ($)"
                type="number"
                value={formData.cost}
                onChange={(e) => setFormData({ ...formData, cost: e.target.value })}
                required
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Valid For (days)"
                type="number"
                value={formData.valid_for_days}
                onChange={(e) => setFormData({ ...formData, valid_for_days: e.target.value })}
                helperText="Leave empty for no expiration"
                inputProps={{ min: 1 }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Valid Class Categories</InputLabel>
                <Select
                  multiple
                  value={formData.valid_class_category_ids}
                  onChange={(e) => setFormData({ ...formData, valid_class_category_ids: e.target.value as string[] })}
                  input={<OutlinedInput label="Valid Class Categories" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((categoryId) => (
                        <Chip key={categoryId} label={getCategoryName(categoryId)} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  {classCategories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      <Checkbox checked={formData.valid_class_category_ids.indexOf(category.id) > -1} />
                      <ListItemText primary={category.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            {editingCredit ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CreditsPage;